import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { DashboardKpis } from '@sellokart/shared';
import { PageHeader } from '@/components/PageHeader';
import { AsyncSection } from '@/components/AsyncSection';
import { Skeleton } from '@/components/States';
import { ReportFilterBar } from '@/components/ReportFilterBar';
import { KpiCard } from '@/components/KpiCard';
import { fetchDashboard, reportKeys } from '@/features/reports/api';
import { useReportFilters } from '@/features/reports/useReportFilters';
import { usePortal } from '@/features/portal/PortalContext';
import { Alert } from '@/components/Alert';
import { DEFAULT_EXPIRY_DAYS, fetchStockAlerts, stockAlertsKeys } from '@/features/reports/stock-alerts-api';

/**
 * Zero orders, and no money moving at all, reads as "nothing happened
 * today", the true empty state. A day with only an expense or a supplier
 * payment still has something to show.
 */
function isEmptyDashboard(kpis: DashboardKpis): boolean {
  return (
    kpis.orders === 0 &&
    kpis.gross === 0 &&
    kpis.returns === 0 &&
    kpis.expenses === 0 &&
    kpis.cashIn === 0 &&
    kpis.cashOut === 0
  );
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * The till's inventory alert banner, portal side: counts only, linking to
 * the full Stock alerts page. Silent while loading, on error, and when there
 * is nothing to flag — it is a nudge, not a report.
 */
function StockAlertBanner({ branchId }: { branchId: string }) {
  const query = useQuery({
    queryKey: stockAlertsKeys.report(branchId, DEFAULT_EXPIRY_DAYS),
    queryFn: ({ signal }) => fetchStockAlerts(branchId, DEFAULT_EXPIRY_DAYS, signal),
  });
  const report = query.data;
  if (!report) return null;
  const parts: string[] = [];
  if (report.expired.length > 0) parts.push(plural(report.expired.length, 'expired lot', 'expired lots'));
  if (report.expiringSoon.length > 0)
    parts.push(
      `${plural(report.expiringSoon.length, 'lot', 'lots')} expiring within ${report.expiringWithinDays} days`,
    );
  if (report.lowStock.length > 0) parts.push(plural(report.lowStock.length, 'product', 'products') + ' low on stock');
  if (parts.length === 0) return null;

  return (
    <Alert tone={report.expired.length > 0 ? 'error' : 'warning'} title="Stock needs attention" className="mb-6">
      {parts.join(' · ')}.{' '}
      <Link to="/stock-alerts" className="font-semibold underline underline-offset-2">
        View stock alerts
      </Link>
    </Alert>
  );
}

const money = (value: number) => value.toFixed(2);

export function DashboardPage() {
  const { profile } = usePortal();
  const canViewCost = profile?.canViewCost !== false;
  const [filters, setFilters] = useReportFilters();
  const query = useQuery({
    queryKey: reportKeys.dashboard(filters),
    queryFn: ({ signal }) => fetchDashboard(filters, signal),
  });

  return (
    <div>
      <PageHeader title={profile?.tenantName ?? 'Dashboard'} description="Today's performance across your branches." />
      <ReportFilterBar
        filters={filters}
        onChange={setFilters}
        branches={profile?.branches ?? []}
        showDocType
        hideDateRange
      />
      <StockAlertBanner branchId={filters.branchId} />
      <AsyncSection
        state={query}
        isEmpty={isEmptyDashboard}
        emptyTitle="No sales yet for this window"
        emptyDescription="Once a till reports a sale for this branch and date, it shows up here."
        skeleton={
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-20" />
            ))}
          </div>
        }
      >
        {(kpis) => (
          <div className="mt-6 space-y-4">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              <KpiCard label="Gross sales" value={kpis.gross.toFixed(2)} />
              <KpiCard label="Returns" value={kpis.returns.toFixed(2)} />
              <KpiCard label="Net sales" value={kpis.net.toFixed(2)} />
              <KpiCard label="Orders" value={String(kpis.orders)} />
              <KpiCard label="Avg order value" value={kpis.avgOrderValue.toFixed(2)} />
              <KpiCard label="Items sold" value={kpis.itemsSold.toFixed(2)} />
            </div>
            {/* Profit, expenses and cash come from P&L and the Day Book, which
                have no POS/Invoice split — so they ignore the doc-type toggle. */}
            {/* Profit is cost-derived: hidden (the server blanks it too) for a
                login without cost access. */}
            <div className={`grid grid-cols-2 gap-4 ${canViewCost ? 'sm:grid-cols-4' : ''}`}>
              {canViewCost ? (
                <>
                  <KpiCard label="Gross profit" value={money(kpis.grossProfit)} />
                  <KpiCard
                    label="Gross margin"
                    value={kpis.grossMargin === null ? '—' : `${kpis.grossMargin.toFixed(1)}%`}
                  />
                </>
              ) : null}
              <KpiCard label="Expenses" value={money(kpis.expenses)} />
              <KpiCard label="Net cash (in − out)" value={money(kpis.netCash)} />
            </div>
          </div>
        )}
      </AsyncSection>
    </div>
  );
}
